import React, { useEffect, useRef, useState } from "react";
import { api, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { BookOpen, Upload, Trash2, FileText, Search, Sparkles, FileUp } from "lucide-react";
import { toast } from "sonner";

export default function Knowledge() {
  const [docs, setDocs] = useState([]);
  const [tab, setTab] = useState("docs");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ title: "", content: "", tags: "", source: "" });
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/knowledge").then((r) => setDocs(r.data.documents));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.content) return toast.error("Título y contenido");
    try {
      await api.post("/knowledge", {
        title: form.title, content: form.content,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        source: form.source || null,
      });
      toast.success("Indexado con embeddings");
      setForm({ title: "", content: "", tags: "", source: "" });
      setDlg(false);
      load();
    } catch {
      toast.error("Error");
    }
  };

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", file.name);
    try {
      const res = await fetch(`${API}/knowledge/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
        body: fd,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      toast.success(`Subido · ${data.chunks} chunks indexados`);
      load();
    } catch (err) {
      toast.error("Fallo al subir");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const search = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.post("/knowledge/search", { query, top_k: 5 });
      setResults(data.results);
      if (data.results.length === 0) toast.warning("Sin coincidencias");
    } finally {
      setSearching(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar del índice?")) return;
    await api.delete(`/knowledge/${id}`);
    toast.success("Eliminado");
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="knowledge-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">RAG SEMÁNTICO · BASE DE CONOCIMIENTO</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Conocimiento</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Documentos autorizados con embeddings vectoriales. Los agentes citan estas fuentes.
          </p>
        </div>
        <div className="flex gap-2">
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            accept=".pdf,.txt,.md,.csv,.json"
            onChange={(e) => uploadFile(e.target.files?.[0])}
            data-testid="doc-file"
          />
          <Button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            variant="outline"
            className="rounded-none border-[var(--border)] hover:border-[var(--accent)]"
            data-testid="doc-upload-file"
          >
            <FileUp className="w-4 h-4 mr-2" /> {uploading ? "Subiendo…" : "Subir PDF/TXT"}
          </Button>
          <Dialog open={dlg} onOpenChange={setDlg}>
            <DialogTrigger asChild>
              <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="btn-add-doc">
                <Upload className="w-4 h-4 mr-2" /> Pegar Texto
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)] max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-xl">Nuevo Documento</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="label-mono">Título</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                         className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="doc-title" />
                </div>
                <div>
                  <Label className="label-mono">Contenido</Label>
                  <Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
                            className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono min-h-[200px]" data-testid="doc-content" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="label-mono">Tags (coma)</Label>
                    <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })}
                           className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="doc-tags" />
                  </div>
                  <div>
                    <Label className="label-mono">Fuente</Label>
                    <Input value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                           className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="doc-source" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={submit} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="doc-submit">
                  Indexar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-[var(--surface)] border border-[var(--border)] p-0 h-auto">
          <TabsTrigger value="docs" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--accent)] font-mono uppercase text-xs" data-testid="kb-tab-docs">
            <FileText className="w-3 h-3 mr-2" /> Documentos ({docs.length})
          </TabsTrigger>
          <TabsTrigger value="search" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--accent)] font-mono uppercase text-xs" data-testid="kb-tab-search">
            <Sparkles className="w-3 h-3 mr-2" /> Búsqueda semántica
          </TabsTrigger>
        </TabsList>

        <TabsContent value="docs" className="mt-4">
          {docs.length === 0 ? (
            <div className="border border-dashed border-[var(--border)] p-16 text-center" data-testid="empty-docs">
              <BookOpen className="w-8 h-8 mx-auto text-[var(--text-dim)] mb-3" />
              <p className="text-sm text-[var(--text-dim)]">Sube el primer SOP, contrato o política.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {docs.map((d) => (
                <div key={d.id} className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`doc-${d.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <FileText className="w-5 h-5 text-[var(--accent)]" />
                    <button onClick={() => del(d.id)} className="text-[var(--text-dim)] hover:text-[var(--danger)]" data-testid={`del-${d.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="font-display font-bold text-lg mb-1 line-clamp-2">{d.title}</div>
                  <p className="text-xs text-[var(--text-dim)] font-mono line-clamp-3 mb-3">
                    {d.content_preview || "—"}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {d.tags?.map((t) => (
                      <Badge key={t} className="rounded-none bg-[var(--surface-2)] border border-[var(--border)] text-white hover:bg-[var(--surface-2)] text-[10px] font-mono">
                        #{t}
                      </Badge>
                    ))}
                  </div>
                  <div className="label-mono flex items-center justify-between">
                    <span>{d.chunks} chunks · {d.status}</span>
                    <span>{new Date(d.uploaded_at).toLocaleDateString("es-ES")}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="search" className="mt-4 space-y-4">
          <div className="border border-[var(--accent)] bg-[var(--surface)] p-5">
            <div className="label-mono mb-3">CONSULTA SEMÁNTICA</div>
            <div className="flex gap-2">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && search()}
                placeholder="ej: ¿Cuál es el descuento máximo autorizado?"
                className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono h-11"
                data-testid="kb-query"
              />
              <Button
                onClick={search}
                disabled={searching || !query.trim()}
                className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80 h-11 px-6"
                data-testid="kb-search"
              >
                <Search className="w-4 h-4 mr-2" /> {searching ? "Buscando…" : "Buscar"}
              </Button>
            </div>
          </div>

          {results.length > 0 && (
            <div className="space-y-2">
              {results.map((r, i) => (
                <div key={i} className="border border-[var(--border)] bg-[var(--surface)] p-4" data-testid={`kb-r-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="rounded-none bg-[var(--accent)]/10 text-[var(--accent)] border border-[var(--accent)]/40 font-mono text-[10px]">
                      score {r.score.toFixed(3)}
                    </Badge>
                    <span className="font-mono text-[10px] text-[var(--text-dim)]">
                      #{r.chunk_index} · {r.document_title}
                    </span>
                  </div>
                  <p className="text-sm text-white leading-relaxed whitespace-pre-wrap">{r.text}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
